import { useCallback, useMemo, useRef } from "react";

import { offset } from "@floating-ui/dom";
import { faExternalLink, faSearch } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "src/base-components/Button";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowOpen,
} from "src/base-components/FloatingWindow/hooks";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { mdService } from "src/base-components/TextEditor/MarkdownService";
import { CaseAction } from "src/cases/caseView/CaseAction";
import { TaskCheckbox } from "src/cases/caseView/components/TaskCheckbox";
import {
  useAvailableCaseTasks,
  useGetCaseTemplateTask,
  useTaskFormValidForCompletion,
} from "src/cases/caseView/hooks";
import {
  CaseDeserialized,
  type CaseTask,
  transformCaseTask,
} from "src/cases/queries/casesTypes";
import { CaseTemplateTaskOutput } from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useCurrentUserId } from "src/store/AuthStore";

type TaskItemProps = {
  task: CaseTask;
  templateTask?: CaseTemplateTaskOutput;
  caseData?: CaseDeserialized;
  index: number;
  totalTasks: number;
  isDisabled?: boolean;
};

const TaskItem: React.FC<TaskItemProps> = ({
  task,
  templateTask,
  caseData,
  index,
  totalTasks,
  isDisabled = false,
}) => {
  const { open } = useFloatingWindowsActions();
  const hasForm = !!templateTask?.form?.fields?.length;
  const buttonRef = useRef<HTMLDivElement>(null);

  const windowId = `task-form-${task.id}`;
  const isWindowOpen = useIsFloatingWindowOpen(windowId);

  const canCompleteTask = useTaskFormValidForCompletion(
    templateTask,
    task.formValues,
  );

  const handleOpenForm = useCallback(() => {
    if (!hasForm) return;

    open(
      {
        id: windowId,
        type: "cm-task",
        target: buttonRef,
        positionOptions: {
          placement: "left-start",
          middleware: [offset({ mainAxis: 60, crossAxis: 0 })],
        },
      },
      {
        task,
        templateTask,
        caseId: caseData?.data.id,
        disabled: isDisabled || isWindowOpen,
      },
    );
  }, [
    open,
    hasForm,
    windowId,
    task,
    templateTask,
    caseData?.data.id,
    isWindowOpen,
    isDisabled,
    buttonRef,
  ]);

  const borderClasses = [
    "flex flex-col gap-2 border border-gray-200 p-3",
    index === 0 ? "rounded-t-lg" : "border-t-0",
    index === totalTasks - 1 ? "rounded-b-lg" : "",
  ].join(" ");

  return (
    <div className={borderClasses} data-loc={`task-item-${task.key}`}>
      <div className="flex w-full items-start gap-2">
        <div className="flex items-center pt-1">
          <TaskCheckbox
            caseData={caseData}
            disabled={
              isDisabled ||
              isWindowOpen ||
              (hasForm && !task.isCompleted && !canCompleteTask)
            }
            task={task}
          />
        </div>

        <div className="flex flex-1 flex-col gap-2">
          <div className="flex flex-col">
            <span
              className="font-inter-semibold-13px text-gray-800"
              data-loc={`task-title-${task.key}`}
            >
              {task.title}
            </span>
            {task.description && (
              <div
                //skipcq: JS-0440
                dangerouslySetInnerHTML={{
                  __html: mdService.toHtml(task.description),
                }}
                className="tktl-markdown font-inter-normal-12px text-gray-500"
                data-loc={`task-description-${task.key}`}
              />
            )}
          </div>

          {hasForm && (
            <div ref={buttonRef}>
              <Button
                dataLoc={`task-fill-form-${task.key}`}
                disabled={isWindowOpen}
                iconRight={faExternalLink}
                size="sm"
                variant="secondary"
                onClick={handleOpenForm}
              >
                Fill form
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const CaseTasks: React.FC<{
  caseData?: CaseDeserialized;
  isLoading?: boolean;
}> = ({ caseData, isLoading }) => {
  const availableTasks = useAvailableCaseTasks(caseData);
  const getTemplateTask = useGetCaseTemplateTask(caseData);
  const capabilities = useCapabilities();
  const currentUserId = useCurrentUserId();

  // Check if user can edit: admin or assigned to the case
  const canEdit = (() => {
    // Admins can always edit
    if (capabilities.cases.canManage) {
      return true;
    }
    // Queue members can edit if they're assigned to the case
    if (!currentUserId) {
      return false;
    }
    const assigneeId = caseData?.data.assignee?.id;
    return assigneeId === currentUserId;
  })();

  const tasks = useMemo(
    () =>
      availableTasks.map((caseTask) =>
        transformCaseTask(caseTask, getTemplateTask),
      ),
    [availableTasks, getTemplateTask],
  );

  if (isLoading) {
    return <LoadingTasks />;
  }

  const noTasks = tasks.length === 0;

  return (
    <div
      className="flex min-h-full flex-col p-4 pb-0"
      data-loc={`case-tasks${noTasks ? "-empty" : ""}`}
    >
      <div
        className="decideScrollbar flex min-h-0 flex-1 flex-col"
        data-loc="task-list"
      >
        {noTasks ? (
          <EmptyState
            description="There are no tasks to complete for this status"
            headline="No tasks to complete"
            icon={faSearch}
          />
        ) : (
          tasks.map((task, index) => {
            const templateTask = getTemplateTask(task.key);

            return (
              <TaskItem
                key={task.id}
                caseData={caseData}
                index={index}
                isDisabled={!canEdit}
                task={task}
                templateTask={templateTask}
                totalTasks={tasks.length}
              />
            );
          })
        )}
      </div>

      <ReviewerInputSection caseData={caseData} isDisabled={!canEdit} />
    </div>
  );
};

const ReviewerInputSection: React.FC<{
  caseData?: CaseDeserialized;
  isDisabled?: boolean;
}> = ({ caseData, isDisabled = false }) => (
  <div className="flex flex-col gap-y-3 border-gray-200 border-t bg-white pb-4">
    <div className="flex border-gray-200 border-b">
      <div className="relative flex px-1 py-3 after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:rounded-full after:bg-indigo-600">
        <span className="font-inter-semibold-13px">Reviewer Input</span>
      </div>
    </div>
    <CaseAction caseData={caseData} isDisabled={isDisabled} />
  </div>
);

const LoadingTasks: React.FC = () => (
  <div className="flex min-h-full flex-col p-4" data-loc="case-tasks-loading">
    <div className="flex flex-col">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className={`border border-gray-200 p-3 ${
            i === 0 ? "rounded-t-lg" : "border-t-0"
          } ${i === 2 ? "rounded-b-lg" : ""}`}
        >
          <div className="flex items-start gap-2">
            <SkeletonPlaceholder height="h-4" width="w-4" />
            <div className="flex-1 space-y-1">
              <SkeletonPlaceholder height="h-4" width="w-32" />
              <SkeletonPlaceholder height="h-3" width="w-48" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);
